/**
 * 🗺️ [Phase v13.41 — 2026-07-14]
 * Dialog carte de validation d'un punch : affiche 2 épingles Google Maps
 *   - 🔴 ROUGE = position GPS du punch (in ou out)
 *   - 🔵 BLEUE = adresse du client (dossier lié au BT)
 *
 * Trace une polyline pointillée bleue en ligne droite entre les 2 points
 * (pas de Google Directions Service pour économiser le quota).
 *
 * Fallbacks :
 *   - Adresse client non géocodée → affiche seulement le point rouge + bannière info
 *   - Google Maps indisponible (gm_authFailure v13.37.1) → affiche 2 liens externes
 *   - Coords punch absentes → dialog impossible à ouvrir (bouton désactivé côté parent)
 *
 * Réutilise le loader Google Maps de GooglePlacesAutocomplete.jsx.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ExternalLink, MapPin, AlertTriangle, Info, RefreshCw, Route, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SEUIL_HORS_ZONE_M = 250; // Cohérent avec backend SEUIL_HORS_ZONE_M
const SCRIPT_ID = 'google-places-script'; // même ID que GooglePlacesAutocomplete → réutilise le loader

// Icônes officielles Google Maps (pas besoin d'assets locaux)
const RED_MARKER_URL = 'https://maps.google.com/mapfiles/ms/icons/red-dot.png';
const BLUE_MARKER_URL = 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png';

let _mapsLoadingPromise = null;

/**
 * Charge la lib Google Maps JS (idempotent, réutilise le script tag existant).
 * Rejette si `gm_authFailure` a déjà été détecté (bascule fallback dégradé).
 */
function loadGoogleMaps() {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR context'));
  if (window.__prqcGmAuthFailed) return Promise.reject(new Error('gm_authFailure'));
  if (window.google?.maps) return Promise.resolve(window.google);
  if (_mapsLoadingPromise) return _mapsLoadingPromise;
  if (!API_KEY) return Promise.reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY manquant'));

  _mapsLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (window.google?.maps) {
        resolve(window.google);
        return;
      }
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}&libraries=places&language=fr`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = (err) => {
      _mapsLoadingPromise = null;
      reject(err);
    };
    document.head.appendChild(script);
  });
  return _mapsLoadingPromise;
}

function formatCoord(v) {
  return typeof v === 'number' ? v.toFixed(5) : '—';
}

/** Distance vol d'oiseau (Haversine, en mètres). */
function haversineMeters(lat1, lon1, lat2, lon2) {
  if (typeof lat1 !== 'number' || typeof lon1 !== 'number' || typeof lat2 !== 'number' || typeof lon2 !== 'number') {
    return null;
  }
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

/** Formate une distance en mètres pour affichage court : « 320 m » ou « 1,2 km ». */
function fmtDistance(m) {
  if (typeof m !== 'number' || !Number.isFinite(m)) return '—';
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1).replace('.', ',')} km`;
}

/** Formate un ISO en « il y a X min ». */
function fmtSinceIso(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.max(1, Math.round(diffMs / 60000));
    if (diffMin < 60) return `il y a ${diffMin} min`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `il y a ${diffH} h`;
    return d.toLocaleDateString('fr-CA');
  } catch { return ''; }
}

function googleMapsExternalUrl(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function googleMapsDirectionsUrl(oLat, oLng, dLat, dLng) {
  return `https://www.google.com/maps/dir/?api=1&origin=${oLat},${oLng}&destination=${dLat},${dLng}&travelmode=driving`;
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {number|null} props.punchLat
 * @param {number|null} props.punchLng
 * @param {'in' | 'out'} props.punchType
 * @param {string} [props.punchHeure]
 * @param {string} [props.punchAdresse]
 * @param {string} [props.employeNom]
 * @param {number|null} [props.clientLat]
 * @param {number|null} [props.clientLng]
 * @param {string} [props.clientAdresse]
 * @param {string} [props.dossierNom]
 * @param {string} [props.punchId]                        — 🆕 v13.50 permet le recalcul routier
 * @param {number|null} [props.distanceRoutiereM]         — 🆕 v13.50 dernière valeur persistée
 * @param {number|null} [props.distanceRoutiereDureeMin]  — 🆕 v13.50 durée trajet
 * @param {string} [props.distanceRoutiereCalcAt]         — 🆕 v13.50 ISO du dernier calcul
 * @param {(res: object) => void} [props.onRecalculDone]  — 🆕 v13.50 callback après recalcul
 */
export function ValidationPunchMapDialog({
  open,
  onClose,
  punchLat,
  punchLng,
  punchType = 'in',
  punchHeure = '',
  punchAdresse = '',
  employeNom = '',
  clientLat = null,
  clientLng = null,
  clientAdresse = '',
  dossierNom = '',
  punchId = null,
  distanceRoutiereM = null,
  distanceRoutiereDureeMin = null,
  distanceRoutiereCalcAt = null,
  onRecalculDone = null,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');

  const hasPunchCoords = typeof punchLat === 'number' && typeof punchLng === 'number';
  const hasClientCoords = typeof clientLat === 'number' && typeof clientLng === 'number';

  const punchLabel = punchType === 'out' ? 'Punch OUT' : 'Punch IN';

  // 🆕 [v13.50 — Item 1] Distance vol d'oiseau calculée localement (Haversine).
  // Sert de base au bouton « Recalculer avec trajet véhicule » qui n'apparaît
  // que si vol d'oiseau > 250 m.
  const distanceVolOiseauM = useMemo(() => {
    if (!hasPunchCoords || !hasClientCoords) return null;
    return haversineMeters(punchLat, punchLng, clientLat, clientLng);
  }, [hasPunchCoords, hasClientCoords, punchLat, punchLng, clientLat, clientLng]);

  // 🆕 [v13.50 — Item 1] État local de la dernière valeur routière (props ou recalculée).
  const [routiereM, setRoutiereM] = useState(distanceRoutiereM);
  const [routiereDureeMin, setRoutiereDureeMin] = useState(distanceRoutiereDureeMin);
  const [routiereCalcAt, setRoutiereCalcAt] = useState(distanceRoutiereCalcAt);
  const [recalculLoading, setRecalculLoading] = useState(false);
  const [recalculError, setRecalculError] = useState('');
  const [nouvelleDeductionMin, setNouvelleDeductionMin] = useState(null);

  // Reset l'état local quand on rouvre le dialog avec un nouveau punch
  useEffect(() => {
    if (!open) return;
    setRoutiereM(distanceRoutiereM);
    setRoutiereDureeMin(distanceRoutiereDureeMin);
    setRoutiereCalcAt(distanceRoutiereCalcAt);
    setRecalculError('');
    setNouvelleDeductionMin(null);
  }, [open, punchId, distanceRoutiereM, distanceRoutiereDureeMin, distanceRoutiereCalcAt]);

  const canRecalculer = !!punchId
    && distanceVolOiseauM !== null
    && distanceVolOiseauM > SEUIL_HORS_ZONE_M;
  const hasRoutiere = typeof routiereM === 'number' && routiereM > 0;

  const handleRecalculer = async () => {
    if (!punchId || recalculLoading) return;
    setRecalculLoading(true);
    setRecalculError('');
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const res = await axios.post(
        `${API_URL}/api/punch/${punchId}/recalculer-distance-vehicule`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 12000 }
      );
      const d = res.data || {};
      if (d.error) {
        setRecalculError(d.error);
        return;
      }
      setRoutiereM(d.distance_routiere_m);
      setRoutiereDureeMin(d.distance_routiere_duree_min);
      setRoutiereCalcAt(d.calc_at);
      setNouvelleDeductionMin(d.nouvelle_deduction_minutes);
      if (typeof onRecalculDone === 'function') {
        try { onRecalculDone(d); } catch { /* silencieux */ }
      }
    } catch (e) {
      setRecalculError(
        e?.response?.data?.detail
        || e?.message
        || "Trajet véhicule indisponible — utilise vol d'oiseau."
      );
    } finally {
      setRecalculLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setStatus('idle');
      setErrorMsg('');
      return;
    }
    if (!hasPunchCoords) {
      setStatus('error');
      setErrorMsg('Coordonnées GPS du punch manquantes');
      return;
    }

    let mounted = true;
    setStatus('loading');

    loadGoogleMaps()
      .then((google) => {
        if (!mounted) return;
        if (!mapContainerRef.current) return;

        const punchPos = { lat: punchLat, lng: punchLng };
        const clientPos = hasClientCoords ? { lat: clientLat, lng: clientLng } : null;

        const map = new google.maps.Map(mapContainerRef.current, {
          center: punchPos,
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        mapRef.current = map;

        // Marker ROUGE : position du punch
        const punchMarker = new google.maps.Marker({
          position: punchPos,
          map,
          icon: RED_MARKER_URL,
          title: `📍 ${punchLabel}${punchHeure ? ' — ' + punchHeure : ''}`,
        });
        const punchInfo = new google.maps.InfoWindow({
          content: `
            <div style="font-family: system-ui, sans-serif; font-size: 13px; max-width: 240px;">
              <p style="margin: 0 0 4px; font-weight: 600; color: #dc2626;">📍 ${punchLabel}</p>
              ${punchHeure ? `<p style="margin: 0; color: #475569;">🕐 ${punchHeure}</p>` : ''}
              ${employeNom ? `<p style="margin: 2px 0 0; color: #475569;">👤 ${employeNom}</p>` : ''}
              ${punchAdresse ? `<p style="margin: 4px 0 0; color: #64748b; font-size: 12px;">${punchAdresse}</p>` : ''}
              <p style="margin: 4px 0 0; color: #94a3b8; font-size: 11px;">
                ${formatCoord(punchLat)}, ${formatCoord(punchLng)}
              </p>
            </div>
          `,
        });
        punchMarker.addListener('click', () => punchInfo.open({ anchor: punchMarker, map }));
        punchMarker.addListener('mouseover', () => punchInfo.open({ anchor: punchMarker, map }));

        if (clientPos) {
          // Marker BLEU : adresse du client
          const clientMarker = new google.maps.Marker({
            position: clientPos,
            map,
            icon: BLUE_MARKER_URL,
            title: `🏠 Adresse client${dossierNom ? ' — ' + dossierNom : ''}`,
          });
          const clientInfo = new google.maps.InfoWindow({
            content: `
              <div style="font-family: system-ui, sans-serif; font-size: 13px; max-width: 260px;">
                <p style="margin: 0 0 4px; font-weight: 600; color: #1d4ed8;">🏠 Adresse du client</p>
                ${dossierNom ? `<p style="margin: 0; color: #1e293b; font-weight: 500;">${dossierNom}</p>` : ''}
                ${clientAdresse ? `<p style="margin: 4px 0 0; color: #475569;">${clientAdresse}</p>` : ''}
                <p style="margin: 4px 0 0; color: #94a3b8; font-size: 11px;">
                  ${formatCoord(clientLat)}, ${formatCoord(clientLng)}
                </p>
              </div>
            `,
          });
          clientMarker.addListener('click', () => clientInfo.open({ anchor: clientMarker, map }));
          clientMarker.addListener('mouseover', () => clientInfo.open({ anchor: clientMarker, map }));

          // Polyline pointillée bleue (ligne droite entre les 2 points)
          const dashedLineSymbol = {
            path: 'M 0,-1 0,1',
            strokeOpacity: 1,
            scale: 4,
          };
          new google.maps.Polyline({
            path: [punchPos, clientPos],
            strokeOpacity: 0,
            icons: [{ icon: dashedLineSymbol, offset: '0', repeat: '20px' }],
            strokeColor: '#1E40AF',
            map,
          });

          // Fit bounds sur les 2 points avec padding confortable
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(punchPos);
          bounds.extend(clientPos);
          map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
        } else {
          // Un seul point → zoom fixe sur le punch
          map.setCenter(punchPos);
          map.setZoom(15);
        }

        setStatus('ready');
      })
      .catch((err) => {
        if (!mounted) return;
        console.warn('[ValidationPunchMapDialog] chargement Google Maps échoué :', err?.message);
        setErrorMsg(String(err?.message || err || 'Erreur inconnue'));
        setStatus('error');
      });

    return () => {
      mounted = false;
    };
  }, [
    open,
    hasPunchCoords,
    hasClientCoords,
    punchLat,
    punchLng,
    clientLat,
    clientLng,
    punchHeure,
    punchAdresse,
    employeNom,
    dossierNom,
    clientAdresse,
    punchLabel,
  ]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden"
        data-testid="validation-punch-map-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-4 h-4 text-red-600" />
            Carte de validation — {punchLabel}
            {punchHeure && <span className="text-slate-500 font-normal">· {punchHeure}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Compare la position du punch avec l&apos;adresse du client pour valider la présence sur le chantier.
          </DialogDescription>

          {/* Légende visuelle */}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200"
              data-testid="legende-punch-rouge"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-red-600 inline-block" aria-hidden />
              📍 Position du punch
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border ${
                hasClientCoords
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-slate-50 text-slate-400 border-slate-200 line-through'
              }`}
              data-testid="legende-client-bleu"
            >
              <span
                className={`w-2.5 h-2.5 rounded-full inline-block ${
                  hasClientCoords ? 'bg-blue-600' : 'bg-slate-300'
                }`}
                aria-hidden
              />
              🏠 Adresse du client
            </span>
            {hasClientCoords && (
              <span className="text-slate-500" data-testid="legende-polyline">
                <span
                  className="inline-block w-6 h-0.5 align-middle bg-transparent"
                  style={{
                    backgroundImage: 'linear-gradient(to right, #1E40AF 50%, transparent 50%)',
                    backgroundSize: '8px 2px',
                    backgroundRepeat: 'repeat-x',
                    height: '2px',
                  }}
                  aria-hidden
                />
                <span className="ml-1">Trajet direct (ligne droite)</span>
              </span>
            )}
          </div>
        </DialogHeader>

        {/* 🆕 [v13.50 — Item 1] Panneau distance : vol d'oiseau + trajet véhicule
            + bouton de recalcul. N'apparaît QUE si les 2 points GPS sont dispos. */}
        {hasPunchCoords && hasClientCoords && distanceVolOiseauM !== null && (
          <div
            className="px-6 py-3 border-b border-slate-200 bg-slate-50/70"
            data-testid="panneau-distance"
          >
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <div className="flex items-center gap-1.5" data-testid="distance-vol-oiseau">
                <span className="text-slate-500 text-xs uppercase tracking-wide">Vol d&apos;oiseau</span>
                <span className={`font-semibold ${distanceVolOiseauM > SEUIL_HORS_ZONE_M ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {fmtDistance(distanceVolOiseauM)}
                </span>
              </div>

              {hasRoutiere ? (
                <div className="flex items-center gap-1.5" data-testid="distance-routiere">
                  <Route className="w-3.5 h-3.5 text-indigo-600" />
                  <span className="text-slate-500 text-xs uppercase tracking-wide">Trajet véhicule</span>
                  <span className="font-semibold text-indigo-700">{fmtDistance(routiereM)}</span>
                  {typeof routiereDureeMin === 'number' && routiereDureeMin > 0 && (
                    <span className="text-xs text-slate-500" data-testid="duree-trajet">
                      · {routiereDureeMin} min
                    </span>
                  )}
                  {routiereCalcAt && (
                    <span className="text-[10px] text-slate-400" data-testid="calc-at-label">
                      · {fmtSinceIso(routiereCalcAt)}
                    </span>
                  )}
                </div>
              ) : (
                canRecalculer && (
                  <span className="text-xs text-slate-500 italic" data-testid="distance-routiere-vide">
                    Trajet véhicule non calculé
                  </span>
                )
              )}

              {canRecalculer && (
                <div className="ml-auto flex items-center gap-2">
                  {nouvelleDeductionMin !== null && nouvelleDeductionMin > 0 && (
                    <span
                      className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-2 py-1"
                      data-testid="nouvelle-deduction"
                    >
                      💡 Déduction proposée : <strong>−{nouvelleDeductionMin} min</strong>
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant={hasRoutiere ? 'outline' : 'default'}
                    onClick={handleRecalculer}
                    disabled={recalculLoading}
                    data-testid="btn-recalculer-distance-vehicule"
                    title={hasRoutiere
                      ? 'Forcer un nouveau calcul du trajet véhicule via Google Maps'
                      : 'Calculer le trajet routier réel via Google Maps'}
                  >
                    {recalculLoading ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        Calcul en cours…
                      </>
                    ) : hasRoutiere ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                        Recalculer
                      </>
                    ) : (
                      <>
                        <Route className="w-3.5 h-3.5 mr-1.5" />
                        Recalculer avec trajet véhicule
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {recalculError && (
              <p
                className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2"
                data-testid="erreur-recalcul-distance"
              >
                {recalculError}
              </p>
            )}
          </div>
        )}

        {/* Bannière fallback client non géocodé */}
        {status === 'ready' && !hasClientCoords && (
          <div
            className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs flex items-start gap-2"
            data-testid="banner-client-non-geocode"
          >
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              Adresse client non géolocalisée — impossible d&apos;afficher l&apos;épingle bleue.
              Vérifie l&apos;adresse du dossier pour activer la comparaison automatique.
            </span>
          </div>
        )}

        {/* Zone carte */}
        <div className="relative bg-slate-100" style={{ minHeight: 400 }}>
          {status === 'loading' && (
            <div
              className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm"
              data-testid="map-loading"
            >
              Chargement de la carte…
            </div>
          )}
          {status === 'error' && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center"
              data-testid="map-fallback-error"
            >
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <p className="text-sm text-slate-700 font-medium">
                Carte Google Maps non disponible
              </p>
              <p className="text-xs text-slate-500 max-w-md">
                Utilise les liens externes ci-dessous pour ouvrir chaque position dans Google Maps.
                {errorMsg && (
                  <span className="block mt-1 text-slate-400 text-[11px]">({errorMsg})</span>
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                {hasPunchCoords && (
                  <a
                    href={googleMapsExternalUrl(punchLat, punchLng)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700"
                    data-testid="fallback-lien-punch"
                  >
                    <ExternalLink className="w-3 h-3" /> Voir le punch
                  </a>
                )}
                {hasClientCoords && (
                  <a
                    href={googleMapsExternalUrl(clientLat, clientLng)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
                    data-testid="fallback-lien-client"
                  >
                    <ExternalLink className="w-3 h-3" /> Voir l&apos;adresse client
                  </a>
                )}
              </div>
            </div>
          )}
          <div
            ref={mapContainerRef}
            className="w-full"
            style={{ height: 460, visibility: status === 'ready' ? 'visible' : 'hidden' }}
            data-testid="map-container"
          />
        </div>

        {/* Footer avec liens externes */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500 flex flex-wrap gap-3">
            {hasPunchCoords && (
              <span data-testid="footer-coord-punch">
                <span className="text-red-600 font-medium">Punch :</span>{' '}
                {formatCoord(punchLat)}, {formatCoord(punchLng)}
              </span>
            )}
            {hasClientCoords && (
              <span data-testid="footer-coord-client">
                <span className="text-blue-600 font-medium">Client :</span>{' '}
                {formatCoord(clientLat)}, {formatCoord(clientLng)}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasPunchCoords && hasClientCoords && (
              <a
                href={googleMapsDirectionsUrl(punchLat, punchLng, clientLat, clientLng)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-700 hover:underline"
                data-testid="lien-directions-externe"
              >
                <ExternalLink className="w-3 h-3" />
                Itinéraire dans Google Maps
              </a>
            )}
            {hasPunchCoords && !hasClientCoords && (
              <a
                href={googleMapsExternalUrl(punchLat, punchLng)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-700 hover:underline"
                data-testid="lien-punch-externe"
              >
                <ExternalLink className="w-3 h-3" />
                Ouvrir le punch dans Google Maps
              </a>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onClose?.()}
              data-testid="btn-fermer-map-dialog"
            >
              Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ValidationPunchMapDialog;
